import { FC, ReactElement, useCallback, useEffect } from 'react';

import { AccessToken } from '@okta/okta-auth-js';
import { useOktaAuth } from '@okta/okta-react';
import axios from 'axios';

import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { clearAuth, setAuth, setLoading, setToken } from '@/store/slices/authSlice';
import { setFilters, setUserSettingsId } from '@/store/slices/preferencesSlice';
import { API_ENDPOINTS, BASE_URL } from '@/utils/constants/apiEndpoints';
import logger from '@/utils/Logger';

import LoadingPage from '@/pages/common/LoadingPage';
import { getVendorDisplayName } from '@/types/matchDevice';
import { FindOneUserResponse, UserRole } from '@/types/user';

const extractRoleFromAccessToken = (accessToken?: AccessToken): UserRole => {
  const roles = accessToken?.claims?.roles;
  if (!roles) throw new Error('User does not have an assigned role');
  return (Array.isArray(roles) ? roles[0] : roles) as UserRole;
};

const mapPractitionerToLabelValue = (practitioner: {
  given?: string;
  family?: string;
  id: string;
}) =>
  practitioner.id === 'null'
    ? { label: '-', value: 'null' }
    : {
        label: `${practitioner?.given ?? ''} ${practitioner?.family ?? ''}`.trim(),
        value: practitioner.id
      };

export const AuthProvider: FC<{ children: ReactElement }> = ({ children }) => {
  const { oktaAuth } = useOktaAuth();
  const dispatch = useAppDispatch();

  const { isAuthenticated, isLoading } = useAppSelector((state) => state.auth);

  // initializeAuth runs once on mount (stable deps: dispatch + oktaAuth never change reference).
  // It must NOT call oktaAuth.getUser() — that hits Okta's /userinfo endpoint, which is
  // rate-limited per org. Because access/ID tokens live in memory only (see oktaConfig.ts),
  // this callback re-runs on every page refresh. Use token claims for all user data instead.
  // See: https://github.com/rhythmmgmt/clinical-portal-2.0/issues/P1-okta-rate-limit
  const initializeAuth = useCallback(async () => {
    try {
      dispatch(setLoading(true));

      const isOktaAuthenticated = await oktaAuth.isAuthenticated();
      if (isOktaAuthenticated) {
        const oktaAccessToken = oktaAuth.getAccessToken() ?? null;

        const { data } = (await axios.get(`${BASE_URL}${API_ENDPOINTS.USERS.CURRENT_USER}`, {
          headers: { Authorization: `Bearer ${oktaAccessToken}` }
        })) as { data: { user: FindOneUserResponse } };

        // Read claims from the in-memory access token rather than calling /userinfo.
        // `sub` is the user's permanent Okta identifier — it never changes between renewals.
        // `roles` is a custom claim injected by the Okta authorization server.
        const accessToken = (await oktaAuth.tokenManager.get('accessToken')) as
          | AccessToken
          | undefined;

        dispatch(
          setAuth({
            isAuthenticated: true,
            user: {
              ...data.user,
              externalId: accessToken?.claims?.sub ?? '',
              role: extractRoleFromAccessToken(accessToken ?? undefined)
            },
            token: oktaAccessToken
          })
        );

        let userSettings = null;
        try {
          const { data: settingsData } = await axios.get(
            `${BASE_URL}${API_ENDPOINTS.USERS.USER_SETTINGS}`,
            { headers: { Authorization: `Bearer ${oktaAccessToken}` } }
          );
          userSettings = settingsData;
        } catch (settingsError) {
          logger.error('Failed to load user settings, filters will be empty', settingsError);
        }

        if (userSettings?.filterId) {
          dispatch(setUserSettingsId(userSettings.filterId));
        }

        // Clear login retry counter on successful authentication
        sessionStorage.removeItem('login-attempt-count');
        sessionStorage.removeItem('login-error-retry');
        sessionStorage.removeItem('login-recovery-pending');

        dispatch(
          setFilters({
            clinics:
              userSettings?.filteredClinics?.map((clinic: { id: string; name: string }) => ({
                label: clinic.name,
                value: clinic.id
              })) ?? [],
            practitioners: userSettings?.filteredPractitioners?.map(mapPractitionerToLabelValue) ?? [],
            signingPractitioners:
              userSettings?.filteredSigningPractitioners?.map(mapPractitionerToLabelValue) ?? [],
            deviceTypes:
              userSettings?.deviceTypes?.map((deviceType: string) => ({
                label: deviceType,
                value: deviceType
              })) ?? [],
            vendors:
              userSettings?.vendors?.map((vendor: string) => ({
                label: getVendorDisplayName(vendor),
                value: vendor
              })) ?? [],
            patientStatuses:
              userSettings?.patientStatuses?.map((status: string) => ({
                label: status,
                value: status
              })) ?? []
          })
        );

        // setAuth internally sets isLoading: false, lifting the loading gate
        dispatch(setLoading(false));
      } else {
        logger.error('AuthProvider mounted but user is not authenticated');
        dispatch(clearAuth());
        dispatch(setLoading(false));
      }
    } catch (error) {
      console.error('Error during authentication:', error);
      dispatch(clearAuth());
      dispatch(setLoading(false));
    }
  }, [dispatch, oktaAuth]);

  useEffect(() => {
    initializeAuth();
  }, [initializeAuth]);

  // Keep the Redux token in sync when Okta silently renews the access token (every ~1 hour).
  // This does NOT re-run initializeAuth — sub and role claims are stable across renewals,
  // so only the raw token string needs updating for outgoing API request headers.
  useEffect(() => {
    const handleTokenRenewed = (key: string, newToken: AccessToken) => {
      if (key === 'accessToken') {
        dispatch(setToken(newToken.accessToken));
      }
    };

    oktaAuth.tokenManager.on('renewed', handleTokenRenewed);
    return () => {
      oktaAuth.tokenManager.off('renewed', handleTokenRenewed);
    };
  }, [dispatch, oktaAuth]);

  if (isLoading || !isAuthenticated) {
    return <LoadingPage />;
  }

  return <>{children}</>;
};
